/** SaaSコネクタ設定。資格情報と突合の間隔。読み書きできるのは idp:admin だけ。 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import type { IdpConnector } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { jst } from "../../lib/format";
import {
  errorText,
  useIdpConnectors,
  useIdpPerms,
  useIdpSetting,
  useInvalidateIdp,
} from "./hooks";

const { Paragraph, Text } = Typography;

type GwsForm = {
  client_id?: string;
  client_secret?: string;
  refresh_token?: string;
  /** 管理対象ルート OU。旧 `test_ou` から改名した(サーバーは旧キーも読む) */
  managed_root_ou?: string;
  /** 権限セットの Google グループを限るドメイン(空なら確認しない) */
  domain?: string;
};
type KeycloakForm = {
  url?: string;
  realm?: string;
  admin_user?: string;
  admin_password?: string;
};
type ReconcileForm = { interval_min?: number };

/** 伏字(•••設定済み)のまま保存しても上書きされない(core.settings_service の規則)。 */
function useSaveSetting(key: string, done: () => void) {
  const api = useApi();
  const invalidate = useInvalidateIdp();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.saveIdpSetting(key, data),
    onSuccess: () => {
      void message.success("保存した(監査ログに記録した)");
      invalidate();
      done();
    },
    onError: (e) => void message.error(errorText(e)),
  });
}

/** 接続テスト。実際に外へ出るのはこのボタンを押したときだけ。 */
function TestButton({ connectorKey }: { connectorKey: string }) {
  const api = useApi();
  const test = useMutation({
    mutationFn: () => api.testIdpConnector(connectorKey),
    onSuccess: (r) =>
      r.ok ? void message.success(r.detail) : void message.warning(r.detail),
    onError: (e) => void message.error(errorText(e)),
  });
  return (
    <Button loading={test.isPending} onClick={() => test.mutate()}>
      接続テスト
    </Button>
  );
}

function GwsCard() {
  const query = useIdpSetting("gws");
  const [form] = Form.useForm<GwsForm>();
  const save = useSaveSetting("gws", () => void query.refetch());

  useEffect(() => {
    if (!query.data) return;
    const v = query.data.value;
    form.setFieldsValue({
      client_id: String(v.client_id ?? ""),
      client_secret: String(v.client_secret ?? ""),
      refresh_token: String(v.refresh_token ?? ""),
      // 旧 `test_ou` しか入っていない設定も画面に出す(保存すると新しい名前に移る)
      managed_root_ou: String(v.managed_root_ou ?? v.test_ou ?? ""),
      domain: String(v.domain ?? ""),
    });
  }, [query.data, form]);

  return (
    <Card size="small" title="Google Workspace" extra={<TestButton connectorKey="gws" />}>
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        管理コンソールで OAuth クライアントを作り、Directory API
        の権限を持つ管理者で取った更新トークンを入れる。
        発行・OU 移動・OU 作成は<Text strong>管理対象ルート OU</Text>
        の中だけで行い、その外のアカウントは停止も移動もしない(安全装置)。
      </Paragraph>
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => save.mutate({ ...v })}
        disabled={save.isPending}
      >
        <Form.Item name="client_id" label="client_id">
          <Input maxLength={300} />
        </Form.Item>
        <Form.Item name="client_secret" label="client_secret" extra="伏字のまま保存すれば変わらない">
          <Input maxLength={300} />
        </Form.Item>
        <Form.Item name="refresh_token" label="refresh_token" extra="伏字のまま保存すれば変わらない">
          <Input maxLength={500} />
        </Form.Item>
        <Form.Item
          name="managed_root_ou"
          label="管理対象ルート OU"
          extra="空なら /PoC。この OU の外には書き込まない(設定キーは managed_root_ou。旧 test_ou も読む)"
        >
          <Input maxLength={200} placeholder="/PoC" />
        </Form.Item>
        <Form.Item
          name="domain"
          label="ドメイン"
          extra="権限セットの Google グループはこのドメインのアドレスに限る(空なら確認しない)"
        >
          <Input maxLength={200} placeholder="example.co.jp" />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={save.isPending}>
            保存
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            最終更新 {jst(query.data?.updated_at ?? null)}
          </Text>
        </Space>
      </Form>
    </Card>
  );
}

function KeycloakCard() {
  const query = useIdpSetting("keycloak");
  const [form] = Form.useForm<KeycloakForm>();
  const save = useSaveSetting("keycloak", () => void query.refetch());
  // 接続先を変えたら、保存済みの管理者パスワードはサーバー側で破棄される(F1)。
  // 伏字のまま送ると「変更なし」なので、欄を空にして入れ直しを促す
  const [savedUrl, setSavedUrl] = useState("");
  const [urlChanged, setUrlChanged] = useState(false);

  useEffect(() => {
    if (!query.data) return;
    const v = query.data.value;
    setSavedUrl(String(v.url ?? ""));
    setUrlChanged(false);
    form.setFieldsValue({
      url: String(v.url ?? ""),
      realm: String(v.realm ?? ""),
      admin_user: String(v.admin_user ?? ""),
      admin_password: String(v.admin_password ?? ""),
    });
  }, [query.data, form]);

  const onValuesChange = (changed: Partial<KeycloakForm>) => {
    if (changed.url === undefined) return;
    const moved = String(changed.url ?? "").trim() !== savedUrl.trim();
    setUrlChanged(moved);
    if (moved) form.setFieldsValue({ admin_password: "" });
  };

  return (
    <Card
      size="small"
      title="Keycloak(SSO グループ割当)"
      extra={<TestButton connectorKey="keycloak" />}
    >
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        アカウントそのものではなく、realm のグループへの所属で利用権を表す。
        グループ名は <Text code>app-カタログ項目名</Text> 固定で、
        <strong>グループは先に Keycloak 側で作っておく</strong>(無ければエラーにする)。
      </Paragraph>
      {urlChanged && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="接続先を変えたので admin_password を入れ直す"
          description="保存済みのパスワードは破棄される(前の接続先の資格情報を新しいホストへ送らないため)。空のまま保存すると、次の接続テストは失敗する。"
        />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => save.mutate({ ...v })}
        onValuesChange={onValuesChange}
        disabled={save.isPending}
      >
        <Form.Item name="url" label="url" extra="例: http://localhost:8082">
          <Input maxLength={300} />
        </Form.Item>
        <Form.Item name="realm" label="realm" extra="利用者が入っている realm">
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="admin_user" label="admin_user" extra="master realm の管理者">
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item
          name="admin_password"
          label="admin_password"
          extra={
            urlChanged
              ? "接続先を変えたので入れ直す(保存済みのものは破棄される)"
              : "伏字のまま保存すれば変わらない"
          }
        >
          <Input maxLength={300} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={save.isPending}>
            保存
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            最終更新 {jst(query.data?.updated_at ?? null)}
          </Text>
        </Space>
      </Form>
    </Card>
  );
}

function ReconcileCard() {
  const query = useIdpSetting("reconcile");
  const [form] = Form.useForm<ReconcileForm>();
  const save = useSaveSetting("reconcile", () => void query.refetch());

  useEffect(() => {
    if (!query.data) return;
    form.setFieldsValue({ interval_min: Number(query.data.value.interval_min ?? 0) });
  }, [query.data, form]);

  return (
    <Card size="small" title="突合の間隔">
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => save.mutate({ interval_min: v.interval_min ?? 0 })}
        disabled={save.isPending}
      >
        <Form.Item
          name="interval_min"
          label="interval_min(分)"
          extra="0 なら定期実行しない(手動だけ)。上限 1440 分"
        >
          <InputNumber min={0} max={1440} step={10} style={{ width: 200 }} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={save.isPending}>
            保存
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            最終更新 {jst(query.data?.updated_at ?? null)}
          </Text>
        </Space>
      </Form>
    </Card>
  );
}

/** カタログ項目から組み立てた一覧。どの項目がどのコネクタで自動化されるか。 */
function ConnectorTable() {
  const { canView } = useIdpPerms();
  const query = useIdpConnectors(canView);

  const columns: ColumnsType<IdpConnector> = [
    { title: "コネクタ", width: 240, render: (_: unknown, row) => row.label },
    {
      title: "扱い",
      width: 180,
      render: (_: unknown, row) => (
        <Tag color={row.mode === "auto" ? "green" : row.mode === "unimplemented" ? "orange" : "default"}>
          {row.mode_label}
        </Tag>
      ),
    },
    {
      title: "設定",
      width: 120,
      render: (_: unknown, row) =>
        row.key === "" ? (
          <Text type="secondary">—</Text>
        ) : row.configured ? (
          <Tag color="green">設定済み</Tag>
        ) : (
          <Tag>未設定</Tag>
        ),
    },
    {
      title: "カタログ項目",
      width: 320,
      render: (_: unknown, row) => row.items.join("、") || "—",
    },
  ];

  return (
    <Card size="small" title="コネクタとカタログ項目">
      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="コネクタ一覧を取得できなかった"
          description={errorText(query.error)}
        />
      )}
      <Table<IdpConnector>
        scroll={{ x: "max-content" }}
        rowKey="key"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        pagination={false}
        locale={{ emptyText: "有効なカタログ項目が無い" }}
      />
      <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
        どの項目をどのコネクタで扱うかは<Link to="/hr/catalog">サービスカタログ</Link>
        で決める(自動にした項目だけコネクタが動く)。
      </Paragraph>
    </Card>
  );
}

export function ConnectorsPage() {
  const { canAdmin } = useIdpPerms();

  return (
    <div>
      <Typography.Title level={3}>SaaSコネクタ設定</Typography.Title>

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="入れた資格情報で実際の SaaS を触る"
        description="自動の項目は、人事のタスクが起きたときにここの資格情報でアカウントを発行・停止する。試すときは検証用テナント(GWS なら管理対象ルート OU の中)を使う。"
      />

      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <ConnectorTable />
        {canAdmin ? (
          <>
            <GwsCard />
            <KeycloakCard />
            <ReconcileCard />
          </>
        ) : (
          <Alert type="info" showIcon message="資格情報と間隔の閲覧・変更には idp:admin が要る" />
        )}
      </Space>
    </div>
  );
}
